import React, { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import {
  AlertCircle,
  CalendarRange,
  ChevronDown,
  Filter,
  RefreshCw,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';
import {
  type FunnelMetricsResponse,
  type User,
  getClinicInfo,
  getDailyFunnel,
  getFunnelBySource,
  getFunnelMetrics,
  getProjections,
  getTimeline,
  listUsers,
} from '../services/api.ts';
import DashboardOverview from '../components/dashboard/DashboardOverview.tsx';
import { getDashboardThemeClasses } from '../components/dashboard/dashboardTheme.ts';
import TagSelector from '../components/TagSelector.tsx';
import { type ClinicGoal, getClinicGoals } from '../services/goalsApi.ts';

function getStartOfCurrentMonth(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
}

function getEndOfCurrentMonth(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0);
}

function formatDateToYYYYMMDD(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return [year, month, day].join('-');
}

function formatDisplayDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-');
  return [day, month, year].join('/');
}

function formatCurrencyBRL(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

interface DashboardShareImageProps {
  clinicName: string;
  clinicLogo: string | null;
  stats: FunnelMetricsResponse;
  startDate: string;
  endDate: string;
}

export const DashboardShareImage: React.FC<DashboardShareImageProps> = ({
  clinicName,
  clinicLogo,
  stats,
  startDate,
  endDate,
}) => (
  <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-white to-slate-100 px-6 py-8">
    <div className="mx-auto max-w-sm">
      <header className="relative overflow-hidden rounded-3xl border border-slate-200/60 bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-600 p-6 shadow-2xl ring-1 ring-black/10">
        <div className="relative z-10">
          <div className="mb-4 flex items-center gap-3">
            {clinicLogo ? (
              <img
                src={clinicLogo}
                className="h-10 w-10 rounded-xl border-2 border-white/30 object-cover shadow-lg"
                alt="Logo da clínica"
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-xl border-2 border-white/30 bg-white/20">
                <span className="text-lg text-white">🏥</span>
              </div>
            )}
            <div>
              <div className="text-sm font-bold tracking-wide text-white/95">{clinicName}</div>
              <div className="text-xs text-white/70">Powered by Sminy.ai</div>
            </div>
          </div>
          <h1 className="mb-2 text-2xl font-black leading-tight tracking-tight text-white">Dashboard de Vendas</h1>
          <p className="text-sm font-medium text-white/85">
            {formatDisplayDate(startDate)} – {formatDisplayDate(endDate)}
          </p>
        </div>
      </header>

      <div className="mt-6 grid grid-cols-2 gap-4">
        {[
          ['Leads', stats.totalLeads, '👥'],
          ['Agendamentos', stats.totalAgendamentos, '📅'],
          ['Comparecimentos', stats.totalComparecimentos, '✅'],
          ['Vendas', stats.totalVendas, '📈'],
        ].map(([label, value, emoji]) => (
          <div key={String(label)} className="rounded-2xl border border-slate-200/60 bg-white p-5 shadow-lg">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
              <span className="text-lg">{emoji}</span>
            </div>
            <div className="text-3xl font-black tracking-tight text-slate-900">
              {Number(value).toLocaleString('pt-BR')}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-2xl border border-emerald-200/60 bg-gradient-to-br from-emerald-50 to-green-50 p-6 shadow-xl">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-emerald-700">Faturamento no período</h2>
        <p className="mt-2 text-4xl font-black tracking-tight text-emerald-900">{formatCurrencyBRL(stats.valorFaturado)}</p>
        <p className="mt-2 text-sm text-emerald-700">Ticket médio: {formatCurrencyBRL(stats.ticketMedio)}</p>
      </div>

      <footer className="mt-8 text-center text-xs font-medium text-slate-400">
        © {new Date().getFullYear()} Sminy.ai
      </footer>
    </div>
  </div>
);

interface CreatorFilterOption {
  value: string;
  label: string;
  createdByType?: string;
  createdByUserId?: number;
}

const BASE_CREATOR_FILTER_OPTIONS: CreatorFilterOption[] = [
  { value: 'all', label: 'Todos' },
];

function numberOrZero(value?: number | null): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function calculateMonthlyRevenueGoal(goals: ClinicGoal[]): number {
  return goals.reduce((total, goal) => {
    if (!goal.active) return total;
    return total + numberOrZero(goal.target_revenue);
  }, 0);
}

interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onDateChange: (startDate: string, endDate: string) => void;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onDateChange,
}) => {
  const { isDark } = useTheme();
  const theme = getDashboardThemeClasses(isDark);
  const [isOpen, setIsOpen] = useState(false);

  const dateRanges = [
    {
      label: 'Hoje',
      getDates: () => {
        const today = formatDateToYYYYMMDD(new Date());
        return { start: today, end: today };
      },
    },
    {
      label: 'Esta semana',
      getDates: () => {
        const today = new Date();
        const start = new Date(today);
        start.setDate(today.getDate() - today.getDay());
        return {
          start: formatDateToYYYYMMDD(start),
          end: formatDateToYYYYMMDD(today),
        };
      },
    },
    {
      label: 'Este mês',
      getDates: () => ({
        start: formatDateToYYYYMMDD(getStartOfCurrentMonth()),
        end: formatDateToYYYYMMDD(getEndOfCurrentMonth()),
      }),
    },
  ];

  return (
    <div className="relative w-full sm:w-auto">
      <button
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        onClick={() => setIsOpen((current) => !current)}
        className={[
          'inline-flex w-full items-center justify-between gap-2 rounded-xl border px-3 py-2 text-sm transition-colors sm:w-auto',
          theme.control,
        ].join(' ')}
      >
        <span className="inline-flex items-center gap-2">
          <CalendarRange className="h-4 w-4 text-sminy-primary" aria-hidden="true" />
          {formatDisplayDate(startDate)} – {formatDisplayDate(endDate)}
        </span>
        <ChevronDown className={['h-4 w-4 transition-transform', isOpen ? 'rotate-180' : ''].join(' ')} aria-hidden="true" />
      </button>

      {isOpen && (
        <div
          role="dialog"
          aria-label="Selecionar período"
          className={[
            'fixed inset-x-3 top-28 z-40 rounded-2xl border p-4 shadow-2xl sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80',
            theme.surface,
            theme.primaryText,
          ].join(' ')}
        >
          <p className={`mb-3 text-xs font-semibold uppercase tracking-[0.08em] ${theme.mutedText}`}>Períodos rápidos</p>
          <div className="grid grid-cols-3 gap-2">
            {dateRanges.map((range) => (
              <button
                key={range.label}
                type="button"
                onClick={() => {
                  const dates = range.getDates();
                  onDateChange(dates.start, dates.end);
                  setIsOpen(false);
                }}
                className={[
                  'rounded-lg border px-2 py-2 text-xs font-medium transition-colors hover:border-sminy-primary',
                  isDark ? 'border-[#27364D] text-[#A6B2C3] hover:text-[#F8FAFC]' : 'border-sminy-border text-sminy-text-secondary hover:text-sminy-primary',
                ].join(' ')}
              >
                {range.label}
              </button>
            ))}
          </div>

          <div className={`mt-4 grid grid-cols-1 gap-3 border-t pt-4 sm:grid-cols-2 ${theme.border}`}>
            <label className={`grid gap-1.5 text-xs ${theme.mutedText}`}>
              Início
              <input
                type="date"
                value={startDate}
                max={endDate}
                onChange={(event) => onDateChange(event.target.value, endDate)}
                className={`min-w-0 rounded-lg border px-2 py-2 text-sm outline-none focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary/15 ${theme.input}`}
              />
            </label>
            <label className={`grid gap-1.5 text-xs ${theme.mutedText}`}>
              Fim
              <input
                type="date"
                value={endDate}
                min={startDate}
                onChange={(event) => onDateChange(startDate, event.target.value)}
                className={`min-w-0 rounded-lg border px-2 py-2 text-sm outline-none focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary/15 ${theme.input}`}
              />
            </label>
          </div>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="mt-4 w-full rounded-lg bg-sminy-primary px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-sminy-primary-hover"
          >
            Aplicar período
          </button>
        </div>
      )}
    </div>
  );
};

interface DashboardFiltersProps {
  clinicId: number;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  mediaSource: string;
  onMediaSourceChange: (source: string) => void;
  creatorFilter: string;
  creatorOptions: CreatorFilterOption[];
  onCreatorFilterChange: (value: string) => void;
  selectedTagIds: number[];
  onSelectedTagIdsChange: (tagIds: number[]) => void;
  onClear: () => void;
}

const DashboardFilters: React.FC<DashboardFiltersProps> = ({
  clinicId,
  isOpen,
  onOpenChange,
  mediaSource,
  onMediaSourceChange,
  creatorFilter,
  creatorOptions,
  onCreatorFilterChange,
  selectedTagIds,
  onSelectedTagIdsChange,
  onClear,
}) => {
  const { isDark } = useTheme();
  const theme = getDashboardThemeClasses(isDark);

  return (
    <div className="relative shrink-0">
    <button
      type="button"
      aria-expanded={isOpen}
      onClick={() => onOpenChange(!isOpen)}
      className={`inline-flex h-10 items-center gap-1.5 rounded-xl border px-3 text-sm font-medium transition-colors ${theme.control}`}
    >
      <Filter className="h-4 w-4" aria-hidden="true" />
      <span className="hidden sm:inline">Filtros</span>
      <ChevronDown className={['h-3.5 w-3.5 transition-transform', isOpen ? 'rotate-180' : ''].join(' ')} aria-hidden="true" />
    </button>

    {isOpen && (
      <div
        data-testid="dashboard-filter-popover"
        className={`fixed inset-x-3 top-40 z-40 max-w-sm rounded-2xl border p-4 shadow-2xl sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80 ${theme.surface}`}
      >
        <label className={`grid gap-2 text-xs font-medium ${theme.mutedText}`}>
          Fonte de mídia
          <select
            value={mediaSource}
            onChange={(event) => onMediaSourceChange(event.target.value)}
            className={`w-full rounded-xl border px-3 py-2 text-sm outline-none focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary/15 ${theme.input}`}
          >
            <option value="">Todas as fontes</option>
            {['Facebook', 'Instagram', 'Google', 'Meta Ads', 'Indicação', 'Fachada', 'Site', 'Orgânico', 'Convênio', 'Carteira de Pacientes', 'Radio', 'TV'].map((source) => (
              <option key={source} value={source}>{source}</option>
            ))}
          </select>
        </label>

        <label className={`mt-3 grid gap-2 text-xs font-medium ${theme.mutedText}`}>
          Usuário da etapa
          <select
            value={creatorFilter}
            onChange={(event) => onCreatorFilterChange(event.target.value)}
            className={`w-full rounded-xl border px-3 py-2 text-sm outline-none focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary/15 ${theme.input}`}
          >
            {creatorOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>

        {clinicId > 0 && (
          <div className={`mt-3 border-t pt-3 ${theme.border}`}>
            <div className="mb-2 flex items-center justify-between gap-3">
              <span className={`text-xs font-medium ${theme.mutedText}`}>Tags</span>
              <span className={`text-[11px] ${theme.mutedText}`}>{selectedTagIds.length} selecionada(s)</span>
            </div>
            <TagSelector
              selectedTags={selectedTagIds}
              onTagsChange={onSelectedTagIdsChange}
              clinicId={clinicId}
              maxTags={10}
              variant="inline"
            />
          </div>
        )}

        <button
          type="button"
          onClick={onClear}
          className={[
            'mt-4 w-full rounded-xl border px-3 py-2 text-sm font-medium transition-colors',
            isDark
              ? 'border-[#27364D] bg-[#27364D]/35 text-[#A6B2C3] hover:bg-[#27364D]/55 hover:text-[#F8FAFC]'
              : 'border-sminy-border bg-slate-50 text-sminy-text-secondary hover:bg-slate-100',
          ].join(' ')}
        >
          Limpar filtros
        </button>
      </div>
    )}
    </div>
  );
};

const DashboardSkeleton: React.FC = () => {
  const { isDark } = useTheme();
  const theme = getDashboardThemeClasses(isDark);

  return (
    <div data-testid="dashboard-loading" className="animate-pulse space-y-5" aria-label="Carregando Visão geral">
      <div className={`grid overflow-hidden rounded-2xl border sm:grid-cols-2 xl:grid-cols-4 ${theme.surface}`}>
        {[0, 1, 2, 3].map((item) => (
          <div key={item} className={`h-28 border-b p-5 xl:border-b-0 xl:border-r ${theme.border}`}>
            <div className={`h-4 w-24 rounded ${theme.skeleton}`} />
            <div className={`mt-3 h-8 w-16 rounded ${theme.skeleton}`} />
          </div>
        ))}
      </div>
      <div className="grid gap-5 xl:grid-cols-12">
        <div className={`h-[410px] rounded-2xl xl:col-span-8 ${theme.skeleton}`} />
        <div className={`h-[410px] rounded-2xl xl:col-span-4 ${theme.skeleton}`} />
      </div>
      <div className="grid gap-5 lg:grid-cols-3">
        {[0, 1, 2].map((item) => <div key={item} className={`h-72 rounded-2xl ${theme.skeleton}`} />)}
      </div>
    </div>
  );
};

const Dashboard: React.FC = () => {
  const { isDark } = useTheme();
  const theme = getDashboardThemeClasses(isDark);
  const dashboardClinicId = parseInt(localStorage.getItem('clinic_id') || '0', 10) || 0;

  const [stats, setStats] = useState<FunnelMetricsResponse | null>(null);
  const [funnelBySource, setFunnelBySource] = useState<Awaited<ReturnType<typeof getFunnelBySource>>>([]);
  const [timelineEvents, setTimelineEvents] = useState<Awaited<ReturnType<typeof getTimeline>>>([]);
  const [projectionsData, setProjectionsData] = useState<Awaited<ReturnType<typeof getProjections>> | null>(null);
  const [dailyFunnelData, setDailyFunnelData] = useState<Awaited<ReturnType<typeof getDailyFunnel>>>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [clinicName, setClinicName] = useState('');
  const [clinicLogo, setClinicLogo] = useState<string | null>(null);
  const [crmUsers, setCrmUsers] = useState<User[]>([]);
  const [creatorFilter, setCreatorFilter] = useState('all');
  const [monthlyRevenueGoalTarget, setMonthlyRevenueGoalTarget] = useState(0);
  const [dateRange, setDateRange] = useState({
    startDate: formatDateToYYYYMMDD(getStartOfCurrentMonth()),
    endDate: formatDateToYYYYMMDD(getEndOfCurrentMonth()),
  });
  const [mediaFilters, setMediaFilters] = useState({ fonte: '' });
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);

  const creatorFilterOptions: CreatorFilterOption[] = [
    ...BASE_CREATOR_FILTER_OPTIONS,
    ...crmUsers.map((user) => {
      const userName = user.name?.trim() || user.email || 'Usuário #' + user.id;
      return {
        value: 'user:' + user.id,
        label: user.is_active === false ? userName + ' (inativo)' : userName,
        createdByType: 'user',
        createdByUserId: user.id,
      };
    }),
  ];
  const selectedCreatorFilter = creatorFilterOptions.find((option) => option.value === creatorFilter)
    || BASE_CREATOR_FILTER_OPTIONS[0];
  const creatorMetricsParams = {
    createdByType: selectedCreatorFilter.createdByType,
    createdByUserId: selectedCreatorFilter.createdByUserId,
  };

  useEffect(() => {
    const fetchClinicInfo = async () => {
      try {
        const info = await getClinicInfo();
        setClinicName(info.name_clinic || info.name || 'Clínica');
        setClinicLogo(info.logo_url || null);
      } catch (clinicError) {
        console.error('Erro ao obter informações da clínica:', clinicError);
        setClinicName('Clínica');
        setClinicLogo(null);
      }
    };

    fetchClinicInfo();
  }, []);

  useEffect(() => {
    const fetchCrmUsers = async () => {
      try {
        if (!dashboardClinicId) {
          setCrmUsers([]);
          return;
        }
        setCrmUsers(await listUsers(dashboardClinicId));
      } catch (usersError) {
        console.error('Erro ao listar usuários para métricas do CRM:', usersError);
        setCrmUsers([]);
      }
    };

    fetchCrmUsers();
  }, [dashboardClinicId]);

  useEffect(() => {
    fetchAllMetrics();
    // Os parâmetros primitivos abaixo representam integralmente os filtros enviados.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.startDate, dateRange.endDate, mediaFilters.fonte, selectedTagIds, creatorFilter]);

  async function fetchAllMetrics() {
    setLoading(true);
    setError('');

    try {
      const clinicId = dashboardClinicId || 1;
      const tagIds = selectedTagIds.length > 0 ? selectedTagIds : undefined;

      const [
        funnelData,
        timelineData,
        projections,
        funnelMain,
        dailyData,
        activeGoals,
      ] = await Promise.all([
        getFunnelBySource(
          clinicId,
          dateRange.startDate,
          dateRange.endDate,
          mediaFilters.fonte || undefined,
          tagIds,
          creatorMetricsParams.createdByType,
          creatorMetricsParams.createdByUserId,
        ),
        getTimeline(
          clinicId,
          dateRange.startDate,
          dateRange.endDate,
          10,
          tagIds,
          creatorMetricsParams.createdByType,
          creatorMetricsParams.createdByUserId,
        ),
        getProjections(
          clinicId,
          tagIds,
          creatorMetricsParams.createdByType,
          creatorMetricsParams.createdByUserId,
        ),
        getFunnelMetrics({
          clinicId,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          ...(mediaFilters.fonte && { fonte: mediaFilters.fonte }),
          ...(tagIds && { tagIds }),
          ...creatorMetricsParams,
        }),
        getDailyFunnel(
          clinicId,
          dateRange.startDate,
          dateRange.endDate,
          mediaFilters.fonte || undefined,
          tagIds,
          creatorMetricsParams.createdByType,
          creatorMetricsParams.createdByUserId,
        ),
        getClinicGoals({
          activeOnly: true,
          startDate: formatDateToYYYYMMDD(getStartOfCurrentMonth()),
          endDate: formatDateToYYYYMMDD(getEndOfCurrentMonth()),
        }).catch((goalError) => {
          console.error('Erro ao carregar metas ativas para o dashboard:', goalError);
          return [];
        }),
      ]);

      setFunnelBySource(funnelData);
      setTimelineEvents(timelineData);
      setProjectionsData(projections);
      setStats(funnelMain);
      setDailyFunnelData(dailyData);
      setMonthlyRevenueGoalTarget(calculateMonthlyRevenueGoal(activeGoals));
    } catch (metricsError) {
      console.error('Erro ao obter métricas:', metricsError);
      setError('Não foi possível carregar as métricas. Verifique sua conexão ou tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  const generateShareImage = async () => {
    if (!stats) return;

    let shareElement: HTMLDivElement | null = null;
    let shareRoot: ReturnType<typeof createRoot> | null = null;

    try {
      shareElement = document.createElement('div');
      shareElement.style.position = 'fixed';
      shareElement.style.top = '-9999px';
      shareElement.style.left = '-9999px';
      shareElement.style.width = '540px';
      shareElement.style.height = '960px';
      shareElement.style.fontFamily = 'system-ui, -apple-system, sans-serif';
      document.body.appendChild(shareElement);

      shareRoot = createRoot(shareElement);
      flushSync(() => {
        shareRoot?.render(
          <DashboardShareImage
            clinicName={clinicName}
            clinicLogo={clinicLogo}
            stats={stats}
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
          />,
        );
      });
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(shareElement, {
        width: 540,
        height: 960,
        scale: 2,
        backgroundColor: '#f8fafc',
        useCORS: true,
        allowTaint: true,
      });

      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'story-dashboard-' + clinicName.replace(/\s+/g, '-') + '-' + formatDateToYYYYMMDD(new Date()) + '.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 'image/png');
    } catch (shareError) {
      console.error('Erro ao gerar imagem:', shareError);
    } finally {
      try {
        shareRoot?.unmount();
      } finally {
        shareElement?.remove();
      }
    }
  };

  void generateShareImage;

  const clearFilters = () => {
    setMediaFilters({ fonte: '' });
    setCreatorFilter('all');
    setSelectedTagIds([]);
    setShowFilters(false);
  };
  const hasActiveFilters = Boolean(
    mediaFilters.fonte
    || selectedTagIds.length > 0
    || selectedCreatorFilter.value !== 'all',
  );

  return (
    <div
      data-testid="dashboard-theme-root"
      data-theme={isDark ? 'dark' : 'light'}
      className={[
      'min-h-screen w-full transition-colors duration-200',
      theme.canvas,
    ].join(' ')}
    >
      <div className="mx-auto max-w-[1440px] p-3 sm:p-5 lg:p-6">
        <header className="relative z-20 mb-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <p className={`mb-1 text-[10px] font-semibold uppercase tracking-[0.12em] ${theme.accentText}`}>Relatórios</p>
              <h1 className="text-2xl font-semibold tracking-[-0.035em] sm:text-[28px]">Visão geral</h1>
              <p className={`mt-1 truncate text-sm ${theme.mutedText}`}>
                Performance comercial da {clinicName || 'sua clínica'}
              </p>
            </div>

            <div className="flex w-full flex-wrap items-center justify-end gap-2 sm:w-auto sm:flex-nowrap">
              <DateRangePicker
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                onDateChange={(startDate, endDate) => setDateRange({ startDate, endDate })}
              />
              <DashboardFilters
                clinicId={dashboardClinicId}
                isOpen={showFilters}
                onOpenChange={setShowFilters}
                mediaSource={mediaFilters.fonte}
                onMediaSourceChange={(fonte) => setMediaFilters({ fonte })}
                creatorFilter={creatorFilter}
                creatorOptions={creatorFilterOptions}
                onCreatorFilterChange={setCreatorFilter}
                selectedTagIds={selectedTagIds}
                onSelectedTagIdsChange={setSelectedTagIds}
                onClear={clearFilters}
              />
              <button
                type="button"
                aria-label="Atualizar dashboard"
                onClick={fetchAllMetrics}
                disabled={loading}
                className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl border transition-colors disabled:cursor-wait disabled:opacity-60 ${theme.control}`}
              >
                <RefreshCw className={['h-4 w-4', loading ? 'animate-spin' : ''].join(' ')} aria-hidden="true" />
              </button>
            </div>
          </div>

          {hasActiveFilters && (
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
              {mediaFilters.fonte && (
                <span className={[
                  'rounded-full px-3 py-1.5 font-medium ring-1',
                  isDark ? 'bg-[#0F1D31] text-[#A6B2C3] ring-[#27364D]' : 'bg-white text-sminy-text-secondary ring-sminy-border',
                ].join(' ')}>
                  Fonte: {mediaFilters.fonte}
                </span>
              )}
              {selectedTagIds.length > 0 && (
                <span className={[
                  'rounded-full px-3 py-1.5 font-medium ring-1',
                  isDark ? 'bg-[#0F1D31] text-[#A6B2C3] ring-[#27364D]' : 'bg-white text-sminy-text-secondary ring-sminy-border',
                ].join(' ')}>
                  Tags: {selectedTagIds.length}
                </span>
              )}
              {selectedCreatorFilter.value !== 'all' && (
                <span className={[
                  'rounded-full px-3 py-1.5 font-medium ring-1',
                  isDark ? 'bg-[#0F1D31] text-[#A6B2C3] ring-[#27364D]' : 'bg-white text-sminy-text-secondary ring-sminy-border',
                ].join(' ')}>
                  Usuário: {selectedCreatorFilter.label}
                </span>
              )}
            </div>
          )}
        </header>

        {error && (
          <div
            role="alert"
            className={[
              'mb-5 flex flex-col gap-3 rounded-2xl border px-4 py-3 sm:flex-row sm:items-center sm:justify-between',
              isDark ? 'border-rose-500/30 bg-rose-500/10 text-rose-300' : 'border-rose-200 bg-rose-50 text-rose-700',
            ].join(' ')}
          >
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 shrink-0" aria-hidden="true" />
              <p className="text-sm">{error}</p>
            </div>
            <button type="button" onClick={fetchAllMetrics} className="self-start rounded-lg border border-current px-3 py-1.5 text-xs font-semibold sm:self-auto">
              Tentar novamente
            </button>
          </div>
        )}

        {loading && <DashboardSkeleton />}

        {!loading && !error && stats && (
          <DashboardOverview
            stats={stats}
            dailyFunnelData={dailyFunnelData}
            funnelBySource={funnelBySource}
            timelineEvents={timelineEvents}
            projectionsData={projectionsData}
            monthlyRevenueTarget={monthlyRevenueGoalTarget}
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
          />
        )}

        {!loading && !error && !stats && (
          <div className={`grid min-h-[420px] place-items-center rounded-2xl border border-dashed p-8 text-center ${theme.surface}`}>
            <div>
              <AlertCircle className="mx-auto h-8 w-8 text-slate-400" aria-hidden="true" />
              <h2 className="mt-3 text-base font-semibold">Sem dados para exibir</h2>
              <p className={`mt-1 text-sm ${theme.mutedText}`}>Ajuste o período ou os filtros e tente novamente.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
